import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getProjets } from "../../../services/projetsServices";

import {
  ensureArray,
  isActiveProjet,
  normalizeProjet,
  sortProjets,
} from "../utils/projetsUtils";

export function useProjetsPage() {
  const bootedRef = useRef(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [projets, setProjets] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [zoomImage, setZoomImage] = useState(false);

  const loadProjets = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await getProjets();

      const rows = sortProjets(
        ensureArray(response).filter(isActiveProjet).map(normalizeProjet)
      );

      setProjets(rows);
    } catch (err) {
      console.error("Erreur chargement projets:", err);
      setProjets([]);
      setError("LOAD_ERROR");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;

    loadProjets();
  }, [loadProjets]);

  useEffect(() => {
    if (!projets.length) {
      setSelectedId(null);
      return;
    }

    setSelectedId((current) => {
      const stillExists = projets.some((projet) => projet.id === current);
      return stillExists ? current : projets[0].id;
    });
  }, [projets]);

  useEffect(() => {
    setZoomImage(false);
  }, [selectedId]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setZoomImage(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const selectedProjet = useMemo(() => {
    return projets.find((projet) => projet.id === selectedId) || projets[0] || null;
  }, [projets, selectedId]);

  const selectProjet = useCallback((id) => {
    setSelectedId(id);

    if (typeof window !== "undefined" && window.innerWidth < 768) {
      window.setTimeout(() => {
        const detail = document.getElementById("projet-detail");
        detail?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 80);
    }
  }, []);

  const openZoom = useCallback(() => {
    setZoomImage(true);
  }, []);

  const closeZoom = useCallback(() => {
    setZoomImage(false);
  }, []);

  const refresh = useCallback(() => {
    setZoomImage(false);
    loadProjets();
  }, [loadProjets]);

  return {
    loading,
    error,
    projets,
    total: projets.length,
    selectedProjet,
    selectedId,
    zoomImage,
    selectProjet,
    openZoom,
    closeZoom,
    refresh,
  };
}